import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Fade,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Camera as TikTokIcon, // Using Camera as placeholder for TikTok
  CameraAlt as SnapchatIcon, // Using CameraAlt as placeholder for Snapchat
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

// Platform configuration with icons
const PLATFORM_CONFIG = {
  facebook: {
    label: 'Facebook',
    icon: FacebookIcon,
    color: '#1877F2',
  },
  instagram: {
    label: 'Instagram',
    icon: InstagramIcon,
    color: '#E1306C',
  },
  twitter: {
    label: 'Twitter',
    icon: TwitterIcon,
    color: '#1DA1F2',
  },
  linkedin: {
    label: 'LinkedIn',
    icon: LinkedInIcon,
    color: '#0077B5',
  },
  youtube: {
    label: 'YouTube',
    icon: YouTubeIcon,
    color: '#FF0000',
  },
  tiktok: {
    label: 'TikTok',
    icon: TikTokIcon,
    color: '#FF0050', // TikTok's vibrant pink/red color
  },
  snapchat: {
    label: 'Snapchat',
    icon: SnapchatIcon,
    color: '#FFFC00',
  },
};

const PlatformSelector = ({ selectedPlatforms = [], onChange, disabled = false }) => {
  const theme = useTheme();

  const handlePlatformToggle = (platform) => {
    if (disabled) return;
    
    const isSelected = selectedPlatforms.includes(platform);
    const newPlatforms = isSelected
      ? selectedPlatforms.filter(p => p !== platform)
      : [...selectedPlatforms, platform];
    
    onChange(newPlatforms);
  };

  return (
    <Box>
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ mb: 1, display: 'block' }}
      >
        Select Platforms
      </Typography>
      
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
          gap: 0.75,
          maxWidth: 400,
        }}
      >
        {Object.entries(PLATFORM_CONFIG).map(([platform, config]) => {
          const Icon = config.icon;
          const isSelected = selectedPlatforms.includes(platform);
          
          return (
            <Tooltip 
              key={platform} 
              title={config.label}
              placement="top"
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 200 }}
            >
              <Box
                onClick={() => handlePlatformToggle(platform)}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.25,
                  p: 0.75,
                  borderRadius: 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: isSelected 
                    ? alpha(config.color, 0.08)
                    : 'background.paper',
                  border: `2px solid`,
                  borderColor: isSelected 
                    ? config.color
                    : theme.palette.mode === 'dark' 
                      ? 'grey.800' 
                      : 'grey.300',
                  opacity: disabled ? 0.5 : 1,
                  '&:hover': {
                    backgroundColor: isSelected
                      ? alpha(config.color, 0.12)
                      : alpha(config.color, 0.04),
                    borderColor: config.color,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[2],
                  },
                }}
              >
                {/* Selected indicator */}
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      bgcolor: config.color,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 14,
                      height: 14,
                    }}
                  >
                    <CheckCircleIcon 
                      sx={{ 
                        fontSize: 10, 
                        color: 'white',
                      }} 
                    />
                  </Box>
                )}
                
                <Icon 
                  sx={{ 
                    fontSize: 18,
                    color: isSelected ? config.color : 'text.secondary',
                    transition: 'color 0.2s ease',
                  }} 
                />
                
                <Typography
                  variant="caption"
                  sx={{
                    color: isSelected ? config.color : 'text.secondary',
                    fontWeight: isSelected ? 600 : 400,
                    transition: 'all 0.2s ease',
                    fontSize: '0.6rem',
                    lineHeight: 1.1,
                  }}
                >
                  {config.label}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      {selectedPlatforms.length > 0 && (
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ mt: 1.5, display: 'block' }}
        >
          {selectedPlatforms.length} platform{selectedPlatforms.length > 1 ? 's' : ''} selected
        </Typography>
      )}
    </Box>
  );
};

export default PlatformSelector;